import { useTranslation } from 'react-i18next';
import { EntregaDoProduto } from '@/components/digital/EntregaDoProduto';
import { ehEntregaDigital } from '@/lib/loja-digital';
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ShoppingCart, Check, Clock, ArrowLeft, Star, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { useCart } from '@/contexts/CartContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { useProducts } from '@/hooks/useProducts';
import { Product } from '@/types';
import PageHero from '@/components/common/PageHero';
import { ProductCard } from '@/components/store/ProductCard';
import { toast } from 'sonner';
import { sanitizeHtml } from '@/lib/sanitize';

import { Caixa } from '@/construtor/caixa';
export default function ProductPage() {
  const { t } = useTranslation();
  const { serverId, categorySlug, productSlug } = useParams<{ serverId?: string; categorySlug?: string; productSlug?: string }>();
  const { addItem } = useCart();
  const { config } = useTheme();
  const { store } = useStore();
  const { products, loading } = useProducts(store?.id, undefined, serverId);
  
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (products.length > 0 && productSlug) {
      const foundProduct = products.find((p) => p.slug === productSlug);
      setProduct(foundProduct || null);
      
      if (foundProduct) {
        const minQty = (foundProduct as any).minQuantity || 1;
        setQuantity(minQty);
        
        const related = products
          .filter((p) => p.categoryId === foundProduct.categoryId && p.id !== foundProduct.id)
          .slice(0, 3);
        setRelatedProducts(related);
      }
    }
  }, [products, productSlug]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(price);
  };

  const handleAddToCart = () => {
    if (product?.soldOut) {
      toast.error(t('store.digital.sold_out_toast'));
      return;
    }
    if (product) {
      const minQty = (product as any).minQuantity || 1;
      if (quantity < minQty) {
        toast.error(`Quantidade mínima: ${minQty}`);
        return;
      }
      addItem(product.id, quantity);
      toast.success(`${quantity}x ${product.name} adicionado ao carrinho!`);
    }
  };

  const handleQuantityChange = (newQty: number) => {
    const minQty = (product as any)?.minQuantity || 1;
    if (newQty >= minQty) {
      setQuantity(newQty);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16">
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">Carregando produto...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16">
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground mb-4">Produto não encontrado.</p>
            <Link to="/loja">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar para a Loja
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const discountPercent = product.originalPrice
    ? Math.round(
        ((product.originalPrice - product.price) / product.originalPrice) * 100
      )
    : 0;

  return (
    <div>
      {/* Hero */}
      <PageHero
        title={product.name}
        breadcrumbs={[
          { label: 'Loja', href: '/loja' },
          ...(serverId && categorySlug ? [{ label: categorySlug, href: `/loja/${serverId}/${categorySlug}` }] : []),
          { label: product.name },
        ]}
      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Caixa id="produto.voltar">
        <Link
          to={serverId && categorySlug ? `/loja/${serverId}/${categorySlug}` : serverId ? `/loja/${serverId}` : '/loja'}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para Loja
        </Link>
        </Caixa>

        {/*
          * Imagem ao lado das informações já no tablet. Antes a imagem só
          * saía de cima a partir de 1024 px, e em 768 px virava um quadrado
          * de 736 px antes de aparecer preço e botão.
          */}
        <div className="grid grid-cols-1 md:grid-cols-5 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Product Image */}
          <div className="md:col-span-2 lg:col-span-1">
            <Caixa id="produto.imagem">
            <Card className="bg-card border-border overflow-hidden mx-auto w-full max-w-sm md:max-w-none">
              <div className="relative aspect-square">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  {product.featured && (
                    <Badge className="bg-accent text-accent-foreground">
                      <Star className="w-3 h-3 mr-1" />
                      Destaque
                    </Badge>
                  )}
                  {discountPercent > 0 && (
                    <Badge variant="destructive">-{discountPercent}%</Badge>
                  )}
                </div>
              </div>
            </Card>
            </Caixa>
          </div>

          {/* Product Details */}
          <div className="md:col-span-3 lg:col-span-2">
            <Caixa id="produto.detalhes" miolo>
            <Card className="bg-card border-border">
              <Caixa id="produto.cabecalho">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-display text-2xl md:text-3xl text-foreground">
                      {product.name}
                    </CardTitle>
                  </div>
                  <div className="text-right">
                    <p className="font-display font-bold text-3xl text-primary">
                      {formatPrice(product.price)}
                    </p>
                    {product.originalPrice && (
                      <p className="text-muted-foreground line-through">
                        {formatPrice(product.originalPrice)}
                      </p>
                    )}
                  </div>
                </div>
              </CardHeader>
              </Caixa>

              <CardContent className="space-y-6">
                {/* Description */}
                <Caixa id="produto.descricao">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Descrição</h3>
                  <div 
                    className="text-muted-foreground prose prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description ?? '') }}
                  />
                </div>
                </Caixa>

                <Separator />

                <EntregaDoProduto product={product} />

                {/* Duration */}
                {(!ehEntregaDigital(product.deliveryKind) || !!product.durationDays) && (
                <Caixa id="produto.duracao">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Duração:</span>
                  <Badge variant="secondary">
                    {product.durationDays
                      ? `${product.durationDays} ${product.durationDays === 1 ? 'dia' : 'dias'}`
                      : 'Permanente'}
                  </Badge>
                </div>
                </Caixa>
                )}

                {/* Quantity Limits Info */}
                {((product as any).minQuantity || (product as any).purchaseLimitPerUser) && (
                  <Caixa id="produto.limites">
                  <div className="text-sm text-muted-foreground space-y-1">
                    {(product as any).minQuantity && (
                      <p>• Quantidade mínima por compra: {(product as any).minQuantity}</p>
                    )}
                    {(product as any).purchaseLimitPerUser && (
                      <p>• Limite por usuário: {(product as any).purchaseLimitPerUser}</p>
                    )}
                  </div>
                  </Caixa>
                )}

                <Separator />

                {/* Quantity Selector */}
                <Caixa id="produto.quantidade">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Quantidade
                  </label>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= ((product as any).minQuantity || 1)}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      type="number"
                      min={(product as any).minQuantity || 1}
                      value={quantity}
                      onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                      className="w-20 text-center"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleQuantityChange(quantity + 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                </Caixa>

                {/* Add to Cart Button */}
                <Caixa id="produto.comprar">
                <Button
                  size="lg"
                  className="w-full bg-primary hover:bg-primary-hover text-primary-foreground"
                  onClick={handleAddToCart}
                  disabled={!!product.soldOut}
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {product.soldOut ? t('store.digital.sold_out') : 'Adicionar ao Carrinho'}
                </Button>
                </Caixa>
              </CardContent>
            </Card>
            </Caixa>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <Caixa id="produto.relacionados">
          <section className="mt-12">
            <h2 className="font-display font-bold text-2xl text-foreground mb-6">
              Produtos Relacionados
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
              {relatedProducts.map((p) => (
                <ProductCard key={p.id} product={p} serverId={serverId} />
              ))}
            </div>
          </section>
          </Caixa>
        )}
      </div>
    </div>
  );
}
